import * as ProRegularSvgIcons from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Api from 'src/api'
import type * as base from 'src/api/base'
import { errorMessage } from 'src/helpers/fns'
import { useAdminAccess, useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useActiveSemester } from 'src/hooks/semesters'
import { Button } from 'src/tailwind/components/Button'
import Swal from 'sweetalert2'
import { mutate } from 'swr'

export function AdministratorCoursesHeaderActions(): JSX.Element {
  const t = useTranslatable()
  const locale = useLocale()
  const navigate = useNavigate()
  const headers = useAuthenticatedHeaders()
  const activeSemester = useActiveSemester()
  const [searchParams] = useSearchParams()
  const semId = searchParams.get('semId') || ''

  const [isDisableChooseRegimeLoading, setIsDisableChooseRegimeLoading] = React.useState(false)

  const { data: semesters } = useApi({
    endpoint: Api.getSemesters,
    params: React.useMemo(() => ({ headers }), [headers]),
  })

  const activeSemesterIdStr =
    activeSemester?.id != null && String(activeSemester.id).length > 0 ? String(activeSemester.id) : ''

  const selectedSemesterId = semId !== '' ? semId : activeSemesterIdStr
  const selectedSemester = React.useMemo(
    () => semesters?.find((s) => String(s.id) === String(selectedSemesterId)) ?? null,
    [semesters, selectedSemesterId]
  )
  const hasAnyFutureSemester = semesters?.some((s) => s.isFuture === true) ?? false
  const canManageCourses = useAdminAccess().canManage('courses')
  const showAddButton =
    canManageCourses &&
    (hasAnyFutureSemester ? selectedSemester?.isFuture === true : selectedSemester?.isActive === true)
  const showDisableChooseRegime = canManageCourses && selectedSemester?.isFuture === true

  const resolvedSemesterId = React.useMemo((): number | null => {
    if (semId !== '') {
      const parsed = parseInt(semId, 10)
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null
    }
    if (activeSemester?.id == null) {
      return null
    }
    const parsedActive = parseInt(String(activeSemester.id), 10)
    return Number.isFinite(parsedActive) && parsedActive > 0 ? parsedActive : null
  }, [semId, activeSemester?.id])

  const handleDisableChooseRegime = React.useCallback(async () => {
    if (resolvedSemesterId == null) {
      toast.error(t('statement:select_semester'))
      return
    }
    const alert = await Swal.fire({
      title: t('common:are_you_sure'),
      text: t('common:disable_choose_regime_confirm'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('common:confirm'),
      cancelButtonText: t('common:cancel'),
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6C757D',
    })
    if (!alert.isConfirmed) {
      return
    }
    setIsDisableChooseRegimeLoading(true)
    try {
      await Api.postAdministratorCoursesDisableChooseRegime({
        headers,
        body: { semId: resolvedSemesterId },
      })
      await mutate((key) => Array.isArray(key) && key[0] === Api.getAdministratorCourses)
      toast.success(t('course:choose_regime_disabled'))
    } catch (error) {
      toast.error(errorMessage(error as base.CommonError))
    } finally {
      setIsDisableChooseRegimeLoading(false)
    }
  }, [headers, resolvedSemesterId, t])

  return (
    <div className="flex flex-wrap items-center gap-2">
      {showAddButton && (
        <Button
          variant="blue"
          onClick={() => {
            const base = `/${locale}/administrator/administration/courses/add`
            const path = selectedSemesterId !== '' ? `${base}?semId=${encodeURIComponent(selectedSemesterId)}` : base
            void navigate(path)
          }}
        >
          <FontAwesomeIcon icon={ProRegularSvgIcons.faPlus} /> {t('common:add')}
        </Button>
      )}
      {showDisableChooseRegime && (
        <Button
          variant="red"
          type="button"
          disabled={isDisableChooseRegimeLoading}
          onClick={() => void handleDisableChooseRegime()}
        >
          {t('common:disable_choose_regime')}
        </Button>
      )}
    </div>
  )
}
